import {Pill} from '@wandb/weave/components/Tag';
import React, {
  FormEvent,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react';

import {InlineComboboxInput} from './InlineComboboxInput';

type InlineComboboxAddItemInputProps = {
  disabled?: boolean;
  onAddItem: (item: string) => void;
  onCancelAddItem: () => void;
  placeholder?: string;
  style: any;
};

export function InlineComboboxAddItemInput({
  disabled,
  onAddItem,
  onCancelAddItem,
  placeholder,
  style,
}: InlineComboboxAddItemInputProps) {
  const fieldName = 'new_item_name';
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    // When the component mounts, focus it for the user automatically. The
    // autoFocus prop might be a reasonable alternative, but it is not
    // guaranteed to work for elements that are dynamically inserted.
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Escape key should close the add-item flow, but escape will propagate up to
  // the MoveRunDrawer, causing the drawer to close as well. We don't want that,
  // so call `stopPropagation` to halt that evnet from bubbling up.
  function handleKey(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onCancelAddItem();
    }
  }

  // Rather than track the value of the input field directly, use a form to
  // capture the input value on submit. Embrace the web's technologies!
  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const itemName = formData.get(fieldName);

    // Because we are capturing the FormData on submit, TypeScript thinks that
    // there's a chance the field might be a File, or undefined. Reassure it
    // that the value is what we think it is.
    if (itemName && typeof itemName === 'string' && itemName.match(/\S+/)) {
      onAddItem(itemName.trim());
    } else {
      onCancelAddItem();
    }
  }

  return (
    <form style={style} onSubmit={handleSubmit} onKeyDown={handleKey}>
      <div className="flex items-center gap-8">
        <InlineComboboxInput
          disabled={disabled}
          placeholder={placeholder ?? 'New item...'}
          ref={inputRef}
          name={fieldName}
          aria-label="New item"
          isNewItem={true}
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
        />
        <div className="flex items-center gap-8 pr-8">
          <div className="flex items-center gap-4 text-moon-650">
            {inputValue ? (
              <>
                <Pill className="rounded-sm" color="moon" label="Enter" /> to
                save
              </>
            ) : (
              <>
                <Pill className="rounded-sm" color="moon" label="Esc" /> to
                cancel
              </>
            )}
          </div>
        </div>
      </div>
    </form>
  );
}
